import { useState } from 'react';
import { useFormikContext } from 'formik';
import {
  Box,
  VStack,
  HStack,
  Input,
  IconButton,
  Button,
  Text,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import { FiTarget } from 'react-icons/fi';
import { VTODocument, TargetMarketEntry } from '../../types';
import { TargetMarketModal } from './TargetMarketModal';

const EMPTY_ENTRY: TargetMarketEntry = { name: '', demographic: '', geographic: '', psychographic: '' };

interface TargetMarketFieldProps {
  isEditing: boolean;
}

/**
 * Multi-entry Target Market / "The List" editor bound to Formik `targetMarketDetails.entries`.
 * Keeps the legacy `targetMarket` string synced (comma-separated names) for PDF/back-compat.
 */
export function TargetMarketField({ isEditing }: TargetMarketFieldProps) {
  const { values, setFieldValue } = useFormikContext<VTODocument>();
  const entries: TargetMarketEntry[] = values.targetMarketDetails?.entries ?? [];

  const [modalIndex, setModalIndex] = useState<number | null>(null);
  const [confirmIndex, setConfirmIndex] = useState<number | null>(null);

  const commit = (next: TargetMarketEntry[]) => {
    setFieldValue('targetMarketDetails', { entries: next });
    // Keep the legacy comma-separated summary in sync for PDF export / back-compat.
    setFieldValue('targetMarket', next.map((e) => e.name).filter(Boolean).join(', '));
  };

  const updateEntry = (idx: number, patch: Partial<TargetMarketEntry>) => {
    commit(entries.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };
  const addEntry = () => commit([...entries, { ...EMPTY_ENTRY }]);
  const removeEntry = (idx: number) => {
    commit(entries.filter((_, i) => i !== idx));
    setConfirmIndex(null);
  };

  const modalEntry = modalIndex !== null ? entries[modalIndex] ?? null : null;

  if (!isEditing) {
    if (entries.length === 0) {
      return (
        <Text fontSize="sm" color="gray.400" fontStyle="italic">
          Not defined
        </Text>
      );
    }
    return (
      <>
        <VStack spacing={1.5} align="stretch">
          {entries.map((entry, idx) => (
            <HStack
              key={idx}
              justify="space-between"
              px={2}
              py={1}
              rounded="md"
              cursor="pointer"
              role="group"
              _hover={{ bg: 'orange.50' }}
              onClick={() => setModalIndex(idx)}
            >
              <Text fontSize="sm" color="gray.700">
                {entry.name || 'Untitled segment'}
              </Text>
              <Text fontSize="xs" color="orange.500" opacity={0} _groupHover={{ opacity: 1 }}>
                View details
              </Text>
            </HStack>
          ))}
        </VStack>
        <TargetMarketModal
          isOpen={modalIndex !== null}
          onClose={() => setModalIndex(null)}
          entry={modalEntry}
          isEditing={false}
          onSave={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <VStack spacing={2} align="stretch">
        {entries.map((entry, idx) => (
          <Box key={idx}>
            <HStack spacing={2}>
              <Input
                value={entry.name}
                onChange={(e) => updateEntry(idx, { name: e.target.value })}
                placeholder="Segment name (e.g., First-time Homebuyers)"
                size="sm"
              />
              <IconButton
                aria-label="Edit segmentation details"
                icon={<FiTarget />}
                size="sm"
                colorScheme="orange"
                variant="ghost"
                onClick={() => setModalIndex(idx)}
              />
              <IconButton
                aria-label="Remove segment"
                icon={<CloseIcon />}
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={() => setConfirmIndex(idx)}
              />
            </HStack>
            {confirmIndex === idx && (
              <HStack mt={1} p={2} bg="red.50" rounded="md" justify="space-between">
                <Text fontSize="xs" color="red.700">
                  Remove this target market segment?
                </Text>
                <HStack spacing={2}>
                  <Button size="xs" colorScheme="red" onClick={() => removeEntry(idx)}>
                    Remove
                  </Button>
                  <Button size="xs" variant="ghost" onClick={() => setConfirmIndex(null)}>
                    Cancel
                  </Button>
                </HStack>
              </HStack>
            )}
          </Box>
        ))}
        <Button leftIcon={<AddIcon />} size="sm" colorScheme="blue" variant="ghost" onClick={addEntry}>
          Add Target Market
        </Button>
      </VStack>
      <TargetMarketModal
        isOpen={modalIndex !== null}
        onClose={() => setModalIndex(null)}
        entry={modalEntry}
        isEditing={true}
        onSave={(updated) => {
          if (modalIndex !== null) updateEntry(modalIndex, updated);
          setModalIndex(null);
        }}
      />
    </>
  );
}
